import React from 'react';
import { Box, Typography, Grid, Container, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const rows = [
  {
    title: 'Complete Student Lifecycle Management',
    desc: 'Maintain digital student profiles from admission to graduation. Track academic performance, attendance trends, library records, and behavioral notes — all in one profile accessible to teachers and administrators.',
    points: ['Student profile with photo ID', 'Academic progression tracking', 'Medical & emergency records', 'Parent contact portal', 'Promotion & TC management'],
    tag: 'Students',
    tagColor: '#2563eb',
    tagBg: '#eff6ff',
    img: 'https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?w=700&h=460&fit=crop&q=80',
    imgAlt: 'Students in classroom',
  },
  {
    title: 'Smart Attendance & Timetable System',
    desc: 'Automate daily attendance marking with biometric or one-click digital entry. Create conflict-free timetables, manage substitution of absent teachers, and notify parents instantly for absent students.',
    points: ['One-click attendance marking', 'SMS/WhatsApp alerts to parents', 'Teacher substitution management', 'Period-wise timetable builder', 'Holiday & leave calendar'],
    tag: 'Attendance',
    tagColor: '#0891b2',
    tagBg: '#e0f2fe',
    img: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=700&h=460&fit=crop&q=80',
    imgAlt: 'Teacher with class register',
    reverse: true,
  },
  {
    title: 'Exam Management & Digital Report Cards',
    desc: 'Configure exams, enter marks, apply grade rules, and automatically generate beautiful digital report cards ready to share with parents. Supports all board formats including CBSE, ICSE, and State boards.',
    points: ['Exam schedule builder', 'Marks entry with validation', 'Auto grade calculation', 'Printable report cards', 'Parent portal access to results'],
    tag: 'Exams',
    tagColor: '#7c3aed',
    tagBg: '#ede9fe',
    img: 'https://images.unsplash.com/photo-1606326608606-aa0b62935f2b?w=700&h=460&fit=crop&q=80',
    imgAlt: 'Student writing exam',
  },
];

const Features = () => (
  <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#fff' }}>
    <Container maxWidth="lg">
      {/* Section header */}
      <Box sx={{ textAlign: 'center', mb: 9 }}>
        <Chip label="CORE FEATURES" size="small" sx={{ bgcolor: '#eff6ff', color: '#2563eb', fontWeight: 800, letterSpacing: 1.5, fontSize: 11, fontFamily: "'Montserrat', sans-serif", mb: 2 }} />
        <Typography variant="h2" sx={{ fontWeight: 900, color: '#0f172a', fontFamily: "'Montserrat', sans-serif", fontSize: { xs: '2rem', md: '2.8rem' } }}>
          Designed Around How Schools Actually Work
        </Typography>
        <Typography sx={{ color: '#64748b', fontSize: 17, mt: 2, fontFamily: "'Montserrat', sans-serif", maxWidth: 600, mx: 'auto', lineHeight: 1.7 }}>
          Every module is purpose-built for Indian schools — with multi-board support and parent-first communication.
        </Typography>
      </Box>

      {rows.map((r, i) => (
        <Grid
          container spacing={6} alignItems="center" key={i}
          sx={{ mb: { xs: 8, md: 11 }, flexDirection: { xs: 'column', md: r.reverse ? 'row-reverse' : 'row' } }}
        >
          {/* ── Text side ── */}
          <Grid item xs={12} md={6}>
            <Box>
              <Chip
                label={r.tag}
                sx={{ bgcolor: r.tagBg, color: r.tagColor, fontWeight: 700, mb: 2.5, fontFamily: "'Montserrat', sans-serif", border: `1px solid ${r.tagColor}30` }}
              />
              <Typography variant="h4" sx={{ fontWeight: 900, color: '#0f172a', mb: 2, fontFamily: "'Montserrat', sans-serif", fontSize: { xs: '1.5rem', md: '2rem' }, lineHeight: 1.25 }}>
                {r.title}
              </Typography>
              <Typography sx={{ color: '#475569', lineHeight: 1.85, mb: 3.5, fontFamily: "'Montserrat', sans-serif", fontSize: 15.5 }}>
                {r.desc}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.3 }}>
                {r.points.map((p, j) => (
                  <Box key={j} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 26, height: 26, borderRadius: '8px', bgcolor: r.tagBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <CheckCircleIcon sx={{ color: r.tagColor, fontSize: 16 }} />
                    </Box>
                    <Typography sx={{ color: '#334155', fontFamily: "'Montserrat', sans-serif", fontSize: 14.5, fontWeight: 500 }}>{p}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Grid>

          {/* ── Image side ── */}
          <Grid item xs={12} md={6}>
            <Box sx={{ position: 'relative' }}>
              {/* Decorative background shape */}
              <Box sx={{
                position: 'absolute',
                inset: '-4% -3%',
                borderRadius: '32px',
                background: `linear-gradient(135deg, ${r.tagBg} 0%, ${r.tagColor}18 100%)`,
                transform: r.reverse ? 'rotate(3deg)' : 'rotate(-3deg)',
                zIndex: 0,
              }} />

              {/* Actual image in clean frame */}
              <Box sx={{
                position: 'relative', zIndex: 1,
                borderRadius: '24px',
                overflow: 'hidden',
                boxShadow: `0 24px 60px ${r.tagColor}20`,
                border: `1.5px solid ${r.tagColor}20`,
              }}>
                <img
                  src={r.img}
                  alt={r.imgAlt}
                  style={{ width: '100%', display: 'block', height: 340, objectFit: 'cover' }}
                />

                {/* Subtle gradient overlay at bottom for readability */}
                <Box sx={{
                  position: 'absolute', bottom: 0, left: 0, right: 0, height: 80,
                  background: `linear-gradient(to top, ${r.tagColor}25, transparent)`,
                  pointerEvents: 'none',
                }} />

                {/* Tag chip on image */}
                <Box sx={{
                  position: 'absolute', bottom: 16, left: 16,
                  bgcolor: '#ffffff', borderRadius: '10px', px: 2, py: 0.8,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                  display: 'flex', alignItems: 'center', gap: 1,
                }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: r.tagColor }} />
                  <Typography sx={{ color: r.tagColor, fontWeight: 800, fontSize: 13, fontFamily: "'Montserrat', sans-serif" }}>{r.tag} Module</Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      ))}
    </Container>
  </Box>
);

export default Features;
